import React, { useContext } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { handleKeyEvents } from 'utils/handlers';
import Button from 'components/shared/Button';
import Slider from 'components/shared/Slider';
import { ShufflerContext } from './Shuffler';
import ShufflerFilterButton from './ShufflerFilterButton';

import type { ShufflerContextType } from './Shuffler';

type ControlsProps = {
  handleShowAdvancedFilters: (show: boolean) => void;
  toggleFilters?: React.ReactEventHandler;
};

export function MobileControls(props: Required<ControlsProps>) {
  const {
    setSelectedFilters,
    selectedFilters,
    filterSearch,
    catalogOptionGroups,
    showWalkthrough,
    staticFilterData,
  } = useContext(ShufflerContext) as ShufflerContextType;

  const { toggleFilters, handleShowAdvancedFilters } = props;

  return (
    <React.Fragment>
      <div className="filter_controls">
        <div className="filter-search-container">
          <div className="filter_search">
            <input
              type="text"
              id="filter-search-input"
              placeholder={staticFilterData?.searchText || 'Loading...'}
              onKeyUp={(e) => filterSearch(e.currentTarget)}
            />
          </div>
          <Button
            type="bare"
            className="filters_button"
            onClick={() => handleShowAdvancedFilters(false)}
          >
            <FontAwesomeIcon icon="sliders-h" size="lg" />
            <span>Hide</span>
          </Button>
        </div>
      </div>

      <div className="selected-filter-buttons">
        <div
          role="button"
          className="show-filters-trigger"
          onClick={(e) => toggleFilters(e)}
          onKeyDown={handleKeyEvents({ open: (e) => toggleFilters(e) })}
        >
          <span>Filters ({catalogOptionGroups.length})</span>
          <FontAwesomeIcon icon="chevron-down" />
        </div>
        {[...selectedFilters].length === 0 && !showWalkthrough && (
          <Button className="launch-walkthrough-button">Help me find a card</Button>
        )}
        {[...selectedFilters].map((filter) => (
          <ShufflerFilterButton key={filter} filter={filter} />
        ))}
        {[...selectedFilters].length > 0 && (
          <Button
            type="bare"
            className="clear-filter-link"
            onClick={() => setSelectedFilters(new Set())}
          >
            Clear All
          </Button>
        )}
      </div>
    </React.Fragment>
  );
}

export function StandardControls(props: ControlsProps) {
  const { handleShowAdvancedFilters } = props;

  const {
    setSelectedFilters,
    selectedFilters,
    handleZoom,
    filterSearch,
    numIcons,
    sortBy,
    setSortBy,
    showWalkthrough,
    setShowWalkthrough,
    staticFilterData,
  } = useContext(ShufflerContext) as ShufflerContextType;

  const [zoomMin, zoomMax] = [2, 8];

  const sort = ({ target: { options, selectedIndex } }: React.ChangeEvent<HTMLSelectElement>) => {
    setSortBy(options[selectedIndex].value);
  };

  return (
    <React.Fragment>
      <div className="filter_controls">
        <Button
          type="bare"
          className="filters_button"
          onClick={() => handleShowAdvancedFilters(false)}
        >
          Hide Filters <FontAwesomeIcon icon="sliders-h" size="lg" />
        </Button>

        <div className="controls_right">
          <div className="shuffler-sort-list">
            <label>
              Sort by
              <select onChange={sort} defaultValue={sortBy}>
                <option value="default">Recommended</option>
                <option value="size_up">Size (smallest)</option>
                <option value="size_down">Size (largest)</option>
                <option value="photo_up">Number of Photos (fewest)</option>
                <option value="photo_down">Number of Photos (most)</option>
              </select>
            </label>
          </div>

          <div className="shuffler-icon-zoom">
            <div
              role="button"
              className="icon-zoom-small"
              onClick={() => handleZoom(zoomMax)}
              onKeyDown={handleKeyEvents({ open: () => handleZoom(zoomMax) })}
              aria-label="Make Icons Smaller"
            />
            <Slider
              id="zoom-shuffler-icons"
              min={zoomMin}
              max={zoomMax}
              value={numIcons}
              step="1"
              onChange={handleZoom}
              title="Change icon size"
            />
            <div
              role="button"
              className="icon-zoom-large"
              onClick={() => handleZoom(zoomMin)}
              onKeyDown={handleKeyEvents({ open: () => handleZoom(zoomMin) })}
              aria-label="Make Icons Larger"
            />
          </div>
        </div>
      </div>

      <div className="filter-search-container">
        <div className="filter_search">
          <input
            type="text"
            id="filter-search-input"
            placeholder={staticFilterData?.searchText || 'Loading...'}
            onKeyUp={(e) => filterSearch(e.currentTarget)}
          />
        </div>

        <div className="selected-filter-buttons">
          {[...selectedFilters].map((filter) => (
            <ShufflerFilterButton key={filter} filter={filter} />
          ))}
          {[...selectedFilters].length === 0 && !showWalkthrough && (
            <Button className="launch-walkthrough-button" onClick={() => setShowWalkthrough(true)}>
              Help me find the right card
            </Button>
          )}
          {[...selectedFilters].length > 0 && (
            <Button type="bare" onClick={() => setSelectedFilters(new Set())}>
              Clear All
            </Button>
          )}
        </div>
      </div>
    </React.Fragment>
  );
}
